// SPDX-License-Identifier: LicenseRef-Proprietary
// Copyright (c) 2026 Invariant Holdings LLC
import { WASM_THEOREM_META } from "./meta.js";
function theoremName(code) {
    return WASM_THEOREM_META[code]?.name ?? code;
}
function refused(action) {
    try {
        action();
        return false;
    }
    catch {
        return true;
    }
}
function probe(theoremCode, governedPathAccepted, forbiddenPathRefused, detail) {
    return {
        theoremCode,
        theoremName: theoremName(theoremCode),
        governedPathAccepted,
        forbiddenPathRefused,
        detail,
    };
}
function validateResourceConsumption(kernel) {
    const handle = kernel.resource_allocate("validation-resource");
    const label = kernel.resource_consume(handle);
    return probe("R1", label === "validation-resource" && !kernel.resource_is_live(handle), refused(() => kernel.resource_consume(handle)), "consume succeeds once and refuses replay");
}
function validateResourceRelease(kernel) {
    const explicit = kernel.managed_create("validation-managed-explicit");
    kernel.managed_release(explicit);
    const useScoped = kernel.managed_create("validation-managed-use");
    kernel.managed_use_and_release(useScoped);
    return probe("R2", kernel.managed_is_released(explicit) &&
        kernel.managed_is_released(useScoped), refused(() => kernel.managed_release(explicit)) &&
        refused(() => kernel.managed_use_and_release(useScoped)), "release succeeds once and both release paths refuse replay");
}
function validatePermit(kernel) {
    const boundary = kernel.permit_create_boundary("trusted-db-readonly");
    const capability = kernel.permit_grant(boundary, "database", "read");
    const delegatedBoundary = kernel.permit_create_boundary("trusted-kernel");
    const delegatedCapability = kernel.permit_grant(delegatedBoundary, "database", "write");
    kernel.permit_revoke_boundary(delegatedBoundary);
    return probe("P", kernel.permit_validate(capability).includes("database:read"), refused(() => kernel.permit_create_boundary("ambient-root")) &&
        refused(() => kernel.permit_validate(delegatedCapability)), "trusted grants validate while ambient authority and revoked boundaries refuse");
}
function validateSeparate(kernel) {
    const data = kernel.separate_as_data("DROP TABLE users");
    const instruction = kernel.separate_as_instruction("SELECT * FROM users WHERE name = $1");
    const query = JSON.parse(kernel.separate_safe_query(instruction, "alice"));
    return probe("S", data.startsWith("DATA:") &&
        instruction.startsWith("INSTR:") &&
        typeof query.template === "string" &&
        Array.isArray(query.params), data !== instruction && String(query.params?.[0]).startsWith("DATA:"), "user input remains data at the query boundary");
}
function validateDataOrigin(kernel) {
    const branded = kernel.origin_brand("payload", "source-a");
    return probe("D1", kernel.origin_verify(branded) === "payload" &&
        kernel.origin_check_source(branded, "source-a"), refused(() => kernel.origin_verify("payload")) &&
        refused(() => kernel.origin_brand("pay⌘load", "source-a")), "origin label round-trips and unbranded or delimiter-confused values refuse");
}
function validateDataReach(kernel) {
    const classified = kernel.reach_classify("secret", 5);
    const lowered = kernel.reach_declassify(classified, 3);
    return probe("D2", kernel.reach_level(classified) === 5 && kernel.reach_level(lowered) === 3, refused(() => kernel.reach_declassify(classified, 6)) &&
        refused(() => kernel.reach_classify("secret", Number.NaN)), "downward flow is explicit and upward or malformed levels refuse");
}
function validatePrecommit(kernel) {
    const committed = kernel.precommit_create("deploy");
    kernel.precommit_commit(committed);
    const cancelled = kernel.precommit_create("rollback");
    kernel.precommit_cancel(cancelled);
    return probe("W", kernel.precommit_state(committed) === "committed" &&
        kernel.precommit_state(cancelled) === "cancelled", refused(() => kernel.precommit_cancel(committed)) &&
        refused(() => kernel.precommit_commit(cancelled)), "committed and cancelled phases refuse retroactive reversal");
}
function validateInvariantEstablishment(kernel) {
    const builder = kernel.inv_builder_create();
    kernel.inv_builder_add_check(builder, "non-empty");
    const state = kernel.inv_establish(builder, "ValidatedState");
    return probe("I1", kernel.inv_state_name(state) === "ValidatedState", refused(() => kernel.inv_builder_add_check(builder, "late-check")) &&
        refused(() => kernel.inv_establish(builder, "ReplayState")), "builder establishes exactly one validated state");
}
function validateInvariantPreservation(kernel) {
    const machine = kernel.inv_machine_create("Draft");
    kernel.inv_machine_allow_transition(machine, "Draft", "Review");
    kernel.inv_machine_transition(machine, "Review");
    return probe("I2", kernel.inv_machine_current_state(machine) === "Review" &&
        kernel.inv_machine_transition_count(machine) === 1, refused(() => kernel.inv_machine_transition(machine, "Approved")), "registered transition advances and unregistered transition refuses");
}
function validateCompose(kernel) {
    const parse = kernel.compose_register_operation("parse", "Request", "Parsed");
    const authorize = kernel.compose_register_operation("authorize", "Parsed", "Authorized");
    const execute = kernel.compose_register_operation("execute", "Sql", "Result");
    const chain = kernel.compose_chain(parse, authorize);
    return probe("C", kernel.compose_validate_chain(chain).includes("Request"), refused(() => kernel.compose_chain(parse, execute)), "compatible chains compose and type mismatches refuse");
}
export function validateWasmKernelContract(kernel, backend) {
    const probes = [
        validateResourceConsumption(kernel),
        validateResourceRelease(kernel),
        validatePermit(kernel),
        validateSeparate(kernel),
        validateDataOrigin(kernel),
        validateDataReach(kernel),
        validatePrecommit(kernel),
        validateInvariantEstablishment(kernel),
        validateInvariantPreservation(kernel),
        validateCompose(kernel),
    ];
    return {
        backend,
        passed: probes.every((entry) => entry.governedPathAccepted && entry.forbiddenPathRefused),
        probes,
    };
}
export function assertWasmKernelContract(kernel, backend) {
    const receipt = validateWasmKernelContract(kernel, backend);
    if (receipt.passed) {
        return receipt;
    }
    const failures = receipt.probes
        .filter((entry) => !entry.governedPathAccepted || !entry.forbiddenPathRefused)
        .map((entry) => `${entry.theoremCode} ${entry.theoremName}: ${entry.detail}`)
        .join("; ");
    throw new Error(`WasmKernel contract validation failed for ${backend}: ${failures}`);
}
//# sourceMappingURL=contract-validation.js.map